if (!customElements.get('ls-richtext-ticker')) {
  class LsRichtextTicker extends HTMLElement {
    constructor() {
      super();
      this.next = this.next.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.updateMask = this.updateMask.bind(this);
      this.handlePointerEnter = this.pause.bind(this);
      this.handlePointerLeave = this.start.bind(this);
      this.handleFocusIn = this.pause.bind(this);
      this.handleFocusOut = this.start.bind(this);
      this.handleMobileChange = this.handleMobileChange.bind(this);
      this.handleReducedMotion = () => {
        if (this.reducedMotionQuery && this.reducedMotionQuery.matches) {
          this.pause();
        } else {
          this.start();
        }
      };
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.ls-richtext__ticker-track');
      if (!this.track) return;

      this.items = Array.from(this.track.querySelectorAll('.ls-richtext__ticker-item'));
      this.itemsPerCopy = parseInt(this.dataset.count, 10) || 0;

      if (this.itemsPerCopy < 1 || this.items.length < this.itemsPerCopy) return;

      this.duration = (parseFloat(this.dataset.duration) || 5) * 1000;
      this.transitionMs = 600;
      this.currentIndex = this.itemsPerCopy;

      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleMobileChange);
      }

      requestAnimationFrame(() => {
        this.setActive();
        this.update(false);
        this.start();
      });

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);

      this.addEventListener('mouseenter', this.handlePointerEnter);
      this.addEventListener('mouseleave', this.handlePointerLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', this.handleReducedMotion);
      }
    }

    disconnectedCallback() {
      this.pause();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mobileQuery && this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleMobileChange);
      }
      if (this.reducedMotionQuery && this.reducedMotionQuery.removeEventListener) {
        this.reducedMotionQuery.removeEventListener('change', this.handleReducedMotion);
      }
    }

    handleMobileChange() {
      this.updateMask();
    }

    handleResize() {
      this.update(false);
    }

    start() {
      this.pause();
      if (this.itemsPerCopy < 2) return;
      if (this.reducedMotionQuery && this.reducedMotionQuery.matches) return;
      this.interval = setInterval(this.next, this.duration);
    }

    pause() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }

    next() {
      this.currentIndex += 1;
      this.setActive();
      this.update(true);

      if (this.currentIndex >= this.itemsPerCopy * 2) {
        const onEnd = () => {
          this.track.removeEventListener('transitionend', onEnd);
          this.currentIndex -= this.itemsPerCopy;
          this.setActive();
          this.update(false);
        };
        this.track.addEventListener('transitionend', onEnd, { once: true });
      }
    }

    setActive() {
      this.items.forEach((item, index) => {
        item.classList.toggle('ls-richtext__ticker-item--active', index === this.currentIndex);
      });
    }

    updateMask() {
      if (!this.mobileQuery || !this.mobileQuery.matches) {
        this.style.removeProperty('--ticker-mask-start');
        this.style.removeProperty('--ticker-mask-end');
        return;
      }

      const activeItem = this.items[this.currentIndex];
      if (!activeItem) return;

      const containerRect = this.getBoundingClientRect();
      const activeRect = activeItem.getBoundingClientRect();
      const containerWidth = containerRect.width;
      if (containerWidth === 0) return;

      const relLeft = activeRect.left - containerRect.left;
      const relRight = activeRect.right - containerRect.left;
      const activeWidth = relRight - relLeft;
      const padPx = 8;
      const fadePx = 24;

      let startPct;
      let endPct;

      if (activeWidth >= containerWidth * 0.95) {
        startPct = 0;
        endPct = 100;
      } else {
        startPct = ((relLeft - padPx - fadePx) / containerWidth) * 100;
        endPct = ((relRight + padPx + fadePx) / containerWidth) * 100;
        startPct = Math.max(0, Math.min(100, startPct));
        endPct = Math.max(0, Math.min(100, endPct));

        if (endPct - startPct < 2) {
          startPct = Math.max(0, startPct - 1);
          endPct = Math.min(100, endPct + 1);
        }

        if (startPct >= endPct) {
          endPct = Math.min(100, startPct + 2);
        }
      }

      this.style.setProperty('--ticker-mask-start', `${startPct}%`);
      this.style.setProperty('--ticker-mask-end', `${endPct}%`);
    }

    update(animate) {
      const containerWidth = this.clientWidth;
      if (containerWidth === 0) return;

      const activeItem = this.items[this.currentIndex];
      if (!activeItem) return;

      const itemCenter = activeItem.offsetLeft + activeItem.offsetWidth / 2;
      const targetX = containerWidth / 2 - itemCenter;

      if (!animate) {
        this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(${targetX}px, 0, 0)`;
        this.track.getBoundingClientRect();
        this.track.style.transition = '';
        this.updateMask();
        return;
      }

      this.track.style.transition = `transform ${this.transitionMs}ms ease`;
      this.track.style.transform = `translate3d(${targetX}px, 0, 0)`;
      this.updateMask();

      const onTransitionEnd = () => {
        this.track.removeEventListener('transitionend', onTransitionEnd);
        this.updateMask();
      };
      this.track.addEventListener('transitionend', onTransitionEnd, { once: true });
    }
  }

  customElements.define('ls-richtext-ticker', LsRichtextTicker);
}
